import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  NgZone,
  PLATFORM_ID,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { CrcPipe } from '../../../shared/pipes/crc.pipe';
import { formatCrc } from '../../../shared/utils/currency.util';
import { HttpErrorResponse } from '@angular/common/http';
import { fromEvent } from 'rxjs';

import { ButtonComponent } from '../../../shared/ui/button/button.component';
import { CountdownComponent } from '../../../shared/ui/countdown/countdown.component';
import { PpCoinComponent } from '../../../shared/ui/pp-coin/pp-coin.component';
import { PaymentService } from '../../../shared/services/payment.service';
import { AuthService } from '../../../shared/services/auth.service';
import { PointsService } from '../../../shared/services/points.service';
import { BlockingLoaderService } from '../../../shared/services/blocking-loader.service';
import { PAYMENT_MAX_ATTEMPTS } from '../../../shared/payments/payment.config';
import {
  PaymentStatus,
  PrepurchaseSessionPaymentState,
} from '../../../shared/payments/payment-provider.interface';
import { PAYMENT_PROVIDER } from '../../../shared/payments/payment-provider.token';
import { OnvoPaymentProvider } from '../../../shared/payments/onvo-payment.provider';
import { CHECKOUT_COPY, PREPURCHASE_COPY, withMonto } from '../checkout-copy';
import { mapEarlyAccessGateError } from '../../../shared/models/prepurchase.model';
import {
  OnvoErrorData,
  OnvoFormErrorsData,
  OnvoGlobal,
  OnvoInstance,
  OnvoSuccessData,
} from '../../../shared/payments/onvo-sdk.types';

/** Intent vivo mínimo que necesita el widget. */
type LiveIntent = { providerIntentId: string; amountColones: number };

/**
 * Fases del paso de pago de precompra. `creating` (bootstrap) → `ready` (widget montable) →
 * `confirming` → `success` (precompra creada) | `pending` (webhook la creará) | `failed` (reintento/salir).
 */
type PaymentPhase = 'creating' | 'ready' | 'confirming' | 'pending' | 'failed' | 'success';

/** Causa del fallo terminal del paso, para elegir copy. */
type PaymentFailureKind = 'create' | 'timeout' | 'network' | 'decline';

/** Errores genéricos de tarjeta (reutilizados del checkout de carrito). */
const CARD_COPY = CHECKOUT_COPY.cardPayment;
/** Copy ESPECÍFICO de la precompra con tarjeta. */
const PRE_COPY = PREPURCHASE_COPY.cardPayment;
/** Reserva flotante (timer/expiración verbatim). */
const RESERVATION = PREPURCHASE_COPY.reservation;

/** Reintentos automáticos de `complete` ante 422 (ONVO aún no marca succeeded tras el onSuccess). */
const COMPLETE_MAX_RETRIES = 2;
const COMPLETE_RETRY_DELAY_MS = 1500;

/** Poller del panel «confirmando»: un tick cada 10 s, máximo 30 (5 min). */
const POLL_INTERVAL_MS = 10_000;
const POLL_MAX_TICKS = 30;

/** Desde dónde se resuelve el estado de la sesión (`resolveSessionState`). */
type SessionOrigin = 'bootstrap' | 'manual' | 'poll';

/** Cómo termina una llamada a `/complete` cuando no da ni éxito ni 409 (422, red, 5xx…). */
interface CompleteOptions {
  /** Reintentos de 422 a `COMPLETE_RETRY_DELAY_MS` (la carrera `onSuccess` ↔ ONVO del widget). */
  readonly maxRetries: number;
  /** Desenlace inconcluso: recibe el status HTTP del último intento (0 = red/timeout). */
  readonly onInconclusive: (status: number) => void;
}

/** URL del SDK web de ONVO (widget hospedado). */
const ONVO_SDK_SRC = 'https://sdk.onvopay.com/sdk.js';

/**
 * Paso de pago con TARJETA del abono (30 %) de una precompra (`/precompra/pago/sesion/:sessionToken`,
 * Bloque 3 del spec docs/specs/precompras-pago-web-plan.md). Análogo a CheckoutPaymentPageComponent,
 * pero el intent cuelga de una `prepurchase_session` (no de un carrito): la `Prepurchase` NO existe
 * hasta que el cobro se aprueba (vía `completePrepurchase` o el webhook). No toca el carrito.
 *
 * Rehidratación por token tras un refresh: `getPrepurchaseSession` restaura importe + countdown de
 * 15 min y resuelve TODOS los estados terminales (consumed → éxito; expired/failed/refund_failed →
 * salida). Un intent ya `approved` (cobrado, aún sin materializar) se COMPLETA en vez de re-montar el
 * widget (evita un segundo cobro), también con la reserva `expired` (D74); `expired` con un cobro
 * `pending`/`processing` pregunta UNA vez a `/complete` (D75). El panel «confirmando» (`pending`) lleva
 * un poller de 30 ticks a 10 s (D79).
 */
@Component({
  selector: 'app-prepurchase-payment-page',
  imports: [CrcPipe, ButtonComponent, CountdownComponent, PpCoinComponent],
  templateUrl: './prepurchase-payment-page.component.html',
  styleUrl: './checkout-payment-page.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class PrepurchasePaymentPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly paymentService = inject(PaymentService);
  private readonly authService = inject(AuthService);
  private readonly pointsService = inject(PointsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly host = inject(ElementRef) as ElementRef<HTMLElement>;
  private readonly platformId = inject(PLATFORM_ID);
  private readonly provider = inject(PAYMENT_PROVIDER);
  private readonly ngZone = inject(NgZone);
  private readonly blockingLoader = inject(BlockingLoaderService);

  /** Promesa cacheada de carga del SDK (compartida entre instancias; null si aún no se cargó). */
  private static sdkPromise: Promise<OnvoGlobal> | null = null;

  /** Nodo dedicado donde ONVO pinta el widget (fuera del control de Angular). */
  private readonly paymentMount = viewChild<ElementRef<HTMLElement>>('paymentMount');

  private onvoInstance: OnvoInstance | null = null;
  private mounted = false;
  private destroyed = false;

  // ── Poller del panel «confirmando» (D79) ──
  private pollActive = false;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private pollTicks = 0;
  /** Tick en curso: desde que arranca hasta que su GET (y un `/complete` suyo) se resuelven. */
  private pollTickOpen = false;
  private pollInFlight = false;
  /** Clic en «Comprobar estado» recibido con un tick en vuelo: espera al resultado del tick. */
  private manualWaiting = false;
  private completeInFlight = false;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  /** `intentStatus` del último `show()` visto por bootstrap o poller (detecta la transición a `approved`). */
  private prevIntentStatus: PaymentStatus | null = null;

  /** Token de la `prepurchase_session` (de la URL). Identidad del pago hasta crear la Prepurchase. */
  private readonly sessionToken = this.route.snapshot.paramMap.get('sessionToken') ?? '';

  protected readonly phase = signal<PaymentPhase>('creating');
  protected readonly intent = signal<LiveIntent | null>(null);
  protected readonly submitting = signal(false);
  protected readonly completing = signal(false);
  protected readonly errorMsg = signal<string | null>(null);

  /** Vencimiento absoluto (ISO) de la reserva de 15 min, para el countdown. Null si no hay reserva viva. */
  protected readonly expiresAt = signal<string | null>(null);

  protected readonly attempts = signal(0);
  protected readonly noRetry = signal(false);
  protected readonly canRetry = computed(
    () => !this.noRetry() && this.attempts() < PAYMENT_MAX_ATTEMPTS,
  );

  protected readonly cardReady = signal(false);
  protected readonly checking = signal(false);

  /**
   * Cobro constatado (pegajoso): se activa al ver `approved` en `show()` o un 409 `cancelled_recovering`.
   * Elige la variante `confirmingReceived*` del panel y evita pintar «reserva expirada» sobre un cliente
   * ya cobrado (la tx `cancelled` recuperada por el webhook no sale en `show()`).
   */
  protected readonly chargeSeen = signal(false);
  /** El poller agotó sus ticks sin desenlace: el panel muestra `pollExhausted`. */
  protected readonly pollExhausted = signal(false);

  /** Abono que devolvió `show()` (sirve aunque no haya intent montable, p. ej. reserva `expired`). */
  private readonly sessionAmount = signal(0);

  /** Monto del abono (30 %, derivado server-side; única fuente de verdad). */
  protected readonly amountColones = computed(
    () => this.intent()?.amountColones ?? this.sessionAmount(),
  );

  /** Título y cuerpo del panel «confirmando»: con cobro constatado o con el pago en vuelo. */
  protected readonly confirmingTitle = computed(() =>
    this.chargeSeen() ? PRE_COPY.confirmingReceivedTitle : PRE_COPY.confirmingInFlightTitle,
  );
  protected readonly confirmingBody = computed(() =>
    this.chargeSeen() ? PRE_COPY.confirmingReceivedBody : PRE_COPY.confirmingInFlightBody,
  );

  /** Copy de cara al cliente para la plantilla. */
  protected readonly cardCopy = CARD_COPY;
  protected readonly preCopy = PRE_COPY;
  protected readonly timerLabel = RESERVATION.timer;
  /** Línea «cuándo suman Perrón Points» (variante tarjeta). */
  protected readonly ppHint = PREPURCHASE_COPY.perronPoints.card;
  /** Sesión iniciada: gatea el bloque de Perrón Points (la precompra de invitado no acumula). */
  protected readonly isAuthenticated = this.authService.isAuthenticated;

  /** Mensaje de éxito verbatim con el abono ya formateado (`{monto}`). */
  protected readonly successMessage = signal<string>('');

  constructor() {
    afterNextRender(() => this.bootstrap());

    // A11y: al cambiar a un panel con encabezado propio, llevamos el foco a su heading.
    effect(() => {
      const p = this.phase();
      if (!isPlatformBrowser(this.platformId)) return;
      if (p === 'creating' || p === 'confirming') return;
      setTimeout(() => {
        this.host.nativeElement
          .querySelector<HTMLElement>('[data-phase-heading]')
          ?.focus({ preventScroll: true });
      });
    });

    // Montaje del widget: SOLO cuando el nodo mount está en el DOM (viewChild resuelto) y hay intent.
    effect(() => {
      const el = this.paymentMount();
      if (!isPlatformBrowser(this.platformId)) return;
      if (this.phase() !== 'ready') return;
      const intent = this.intent();
      if (!intent || !el || this.mounted) return;
      this.mounted = true;
      void this.mountWidget(el.nativeElement);
    });

    // Overlay global BLOQUEANTE durante la materialización de la precompra en el backend (post-3DS).
    effect(() => {
      if (!isPlatformBrowser(this.platformId)) return;
      if (this.completing()) {
        this.blockingLoader.show();
      } else {
        this.blockingLoader.hide();
      }
    });

    // Aviso del navegador si se intenta recargar/cerrar la pestaña en pleno pago.
    afterNextRender(() => {
      fromEvent<BeforeUnloadEvent>(window, 'beforeunload')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((e) => {
          if (!this.submitting()) return;
          e.preventDefault();
          e.returnValue = '';
        });
    });

    this.destroyRef.onDestroy(() => {
      this.destroyed = true;
      this.stopPolling();
      if (this.retryTimer !== null) clearTimeout(this.retryTimer);
      this.retryTimer = null;
      this.blockingLoader.hide();
      this.teardownWidget();
    });
  }

  /**
   * Bootstrap por token: recupera el estado vivo de la reserva y lo resuelve con `resolveSessionState`
   * (misma función que `checkSession` y el poller).
   */
  private bootstrap(): void {
    if (!this.sessionToken) {
      this.fail('create', PRE_COPY_CREATE_FAIL);
      return;
    }

    this.enterPhase('creating');
    this.paymentService
      .getPrepurchaseSession(this.sessionToken)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (s) => this.resolveSessionState(s, 'bootstrap'),
        error: (err: unknown) => this.resolveTerminalHttp(err, 'create'),
      });
  }

  /**
   * Resuelve el estado que devuelve `show()` para los tres orígenes (tabla de reglas de D79):
   * `bootstrap` (carga de la página: el único que monta o crea un widget y que completa por sí mismo),
   * `manual` («Comprobar estado») y `poll` (tick del poller). `manual`/`poll` solo observan: «siguen
   * confirmando», salvo un estado terminal o la transición a `approved` (que el poller completa).
   */
  private resolveSessionState(s: PrepurchaseSessionPaymentState, origin: SessionOrigin): void {
    const intentStatus = s.intentStatus ?? null;
    const previous = this.prevIntentStatus;
    // Un «Comprobar estado» manual no consume la transición: la ve el siguiente tick del poller.
    if (origin !== 'manual') this.prevIntentStatus = intentStatus;

    this.sessionAmount.set(s.amountColones);
    if (intentStatus === 'approved') this.chargeSeen.set(true);
    if (origin === 'bootstrap') {
      // D82: una reserva vencida no pinta countdown (ni sobre un `expires_at` pasado ni sobre uno futuro).
      this.expiresAt.set(s.status === 'expired' ? null : (s.expiresAt ?? null));
    }

    switch (s.status) {
      case 'consumed':
        this.showSuccess(s.amountColones);
        return;
      case 'failed':
        // D80: `/complete` sale por el cierre de la sesión (sin GET a ONVO) y da el código exacto del reembolso.
        this.completeOnce(() => this.failWithoutRetry(PRE_COPY.refundPending));
        return;
      case 'refund_failed':
        this.failWithoutRetry(PRE_COPY.refundFailed);
        return;
      case 'expired':
        this.resolveExpired(intentStatus, previous, origin);
        return;
      case 'pending':
        this.resolvePending(s, intentStatus, previous, origin);
        return;
    }
  }

  /** Reserva `expired`: depende de si hay un cobro vivo (D73) y de quién pregunta. */
  private resolveExpired(
    intentStatus: PaymentStatus | null,
    previous: PaymentStatus | null,
    origin: SessionOrigin,
  ): void {
    if (intentStatus === 'approved') {
      if (origin === 'bootstrap') {
        // D74: igual que `pending` + `approved`.
        this.completeApproved();
      } else {
        this.completeOnTransition(previous, intentStatus, origin);
        this.keepConfirming(origin);
      }
      return;
    }

    if (intentStatus === 'pending' || intentStatus === 'processing') {
      if (origin === 'bootstrap') {
        // D75: `/complete` UNA vez (es autoritativo), sin reintento de 422 (no hay carrera de widget).
        this.completeOnce((status) => {
          if (status === 422 && intentStatus === 'pending') {
            // Casi siempre una reserva abandonada sin pagar; el residuo (3DS en vuelo) lo cierran el
            // webhook o el reconcile. Terminal y sin poller, con un copy que es verdad en ambos casos.
            this.failWithoutRetry(PRE_COPY.expiredMaybePaid);
          } else {
            this.enterConfirming();
          }
        });
      } else {
        this.keepConfirming(origin);
      }
      return;
    }

    // Sin cobro vivo: `RESERVATION.expired`, salvo que ya constatamos un cobro (tx `cancelled` de A8,
    // que `show()` no expone): entonces se sigue confirmando hasta `consumed`/`failed`/agotarse.
    if (origin !== 'bootstrap' && this.chargeSeen()) {
      this.keepConfirming(origin);
      return;
    }
    this.failWithoutRetry(RESERVATION.expired);
  }

  /** Reserva `pending` vigente: montar/crear intent (bootstrap) o seguir confirmando (manual/poll). */
  private resolvePending(
    s: PrepurchaseSessionPaymentState,
    intentStatus: PaymentStatus | null,
    previous: PaymentStatus | null,
    origin: SessionOrigin,
  ): void {
    if (origin !== 'bootstrap') {
      this.completeOnTransition(previous, intentStatus, origin);
      this.keepConfirming(origin);
      return;
    }

    if (intentStatus === 'approved') {
      if (s.providerIntentId) {
        this.intent.set({ providerIntentId: s.providerIntentId, amountColones: s.amountColones });
      }
      this.completeApproved();
      return;
    }
    // Intent vivo → montar widget; sin intent → crear uno.
    if (s.providerIntentId) {
      this.resumeIntent({ providerIntentId: s.providerIntentId, amountColones: s.amountColones });
      return;
    }
    this.createIntent();
  }

  /** Cobro `approved` visto al cargar: completar con el overlay bloqueante y los reintentos de 422. */
  private completeApproved(): void {
    this.startCompleting();
    this.completeWithRetry(0);
  }

  /** El poller llama a `/complete` solo cuando `intentStatus` PASA a `approved` (una vez, sin solapes). */
  private completeOnTransition(
    previous: PaymentStatus | null,
    current: PaymentStatus | null,
    origin: SessionOrigin,
  ): void {
    if (origin !== 'poll' || current !== 'approved' || previous === 'approved') return;
    if (this.completeInFlight) return;
    // Sin `startCompleting()`: el panel no cambia ni sale el overlay. 422 o red → el poller sigue.
    this.completeOnce(() => undefined);
  }

  /** Sigue en el panel «confirmando»; en `manual` además informa de que aún no hay desenlace. */
  private keepConfirming(origin: SessionOrigin): void {
    this.enterPhase('pending');
    if (origin === 'manual') {
      this.checking.set(false);
      this.errorMsg.set(PRE_COPY.stillConfirming);
    }
  }

  /** Entra en el panel «confirmando» y arranca el poller (solo en navegador). */
  private enterConfirming(): void {
    this.submitting.set(false);
    this.completing.set(false);
    this.checking.set(false);
    this.enterPhase('pending');
    this.startPolling();
  }

  /** Cambia de fase; al salir de `pending` para el poller y el botón «Comprobar estado» deja de esperar. */
  private enterPhase(phase: PaymentPhase): void {
    this.phase.set(phase);
    if (phase !== 'pending') {
      this.stopPolling();
      this.checking.set(false);
    }
  }

  /** Crea el intent del 30 % sobre la reserva y monta el widget. */
  private createIntent(): void {
    this.paymentService
      .createPrepurchaseIntent(this.sessionToken)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (r) => this.resumeIntent({ providerIntentId: r.providerIntentId, amountColones: r.amountColones }),
        error: (err: unknown) => this.resolveTerminalHttp(err, 'create'),
      });
  }

  /** Deja el widget listo para el pago (fase `ready`; el effect lo monta cuando el nodo existe). */
  private resumeIntent(intent: LiveIntent): void {
    this.intent.set(intent);
    this.enterPhase('ready');
  }

  /** CTA «Pagar»: dispara la tokenización + confirmación del widget (`manualSubmit: true`). */
  protected onPayClick(): void {
    if (this.submitting() || !this.cardReady() || !this.onvoInstance) return;
    this.submitting.set(true);
    this.errorMsg.set(null);
    this.enterPhase('confirming');
    this.onvoInstance.submitPayment();
  }

  private get onvoPublishableKey(): string {
    return this.provider.id === 'onvo'
      ? (this.provider as OnvoPaymentProvider).publishableKey
      : '';
  }

  private loadSdk(): Promise<OnvoGlobal> {
    if (!isPlatformBrowser(this.platformId)) return Promise.reject(new Error('onvo-ssr'));
    if (window.onvo) return Promise.resolve(window.onvo);
    if (PrepurchasePaymentPageComponent.sdkPromise) return PrepurchasePaymentPageComponent.sdkPromise;

    const promise = new Promise<OnvoGlobal>((resolve, reject) => {
      const onReady = () =>
        window.onvo ? resolve(window.onvo) : reject(new Error('onvo-undefined'));
      const onError = () => reject(new Error('onvo-load-failed'));

      const existing = document.querySelector<HTMLScriptElement>(`script[src="${ONVO_SDK_SRC}"]`);
      if (existing) {
        if (window.onvo) return resolve(window.onvo);
        existing.addEventListener('load', onReady, { once: true });
        existing.addEventListener('error', onError, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = ONVO_SDK_SRC;
      script.async = true;
      script.addEventListener('load', onReady, { once: true });
      script.addEventListener('error', onError, { once: true });
      document.head.appendChild(script);
    }).catch((err) => {
      PrepurchasePaymentPageComponent.sdkPromise = null;
      throw err;
    });

    PrepurchasePaymentPageComponent.sdkPromise = promise;
    return promise;
  }

  private async mountWidget(el: HTMLElement): Promise<void> {
    const intent = this.intent();
    if (!intent) {
      this.mounted = false;
      return;
    }

    const publicKey = this.onvoPublishableKey;
    if (!publicKey) {
      this.fail('create');
      return;
    }

    try {
      const onvo = await this.loadSdk();
      if (this.destroyed) return;
      this.ngZone.runOutsideAngular(() => {
        el.replaceChildren();
        this.onvoInstance = onvo.pay({
          publicKey,
          paymentIntentId: intent.providerIntentId,
          paymentType: 'one_time',
          manualSubmit: true,
          locale: 'es',
          onSuccess: (data) => this.ngZone.run(() => this.onWidgetSuccess(data)),
          onError: (data) => this.ngZone.run(() => this.onWidgetError(data)),
          onFormErrors: (data) => this.ngZone.run(() => this.onWidgetFormErrors(data)),
        });
        this.onvoInstance.render('[data-payment-mount]');
      });
      this.cardReady.set(true);
    } catch {
      this.mounted = false;
      if (!this.destroyed) this.fail('create');
    }
  }

  private onWidgetSuccess(_data: OnvoSuccessData): void {
    this.startCompleting();
    this.completeWithRetry(0);
  }

  /** Pasa a la fase de materialización bloqueante. */
  private startCompleting(): void {
    this.submitting.set(true);
    this.completing.set(true);
    this.enterPhase('confirming');
  }

  /** `/complete` con los reintentos de 422 de la carrera del widget; inconcluso → «confirmando» + poller. */
  private completeWithRetry(attempt: number): void {
    this.runComplete(
      { maxRetries: COMPLETE_MAX_RETRIES, onInconclusive: () => this.enterConfirming() },
      attempt,
    );
  }

  /** `/complete` UNA vez, sin reintento de 422; el llamador decide qué hacer con un desenlace inconcluso. */
  private completeOnce(onInconclusive: (status: number) => void): void {
    this.runComplete({ maxRetries: 0, onInconclusive });
  }

  private runComplete(opts: CompleteOptions, attempt = 0): void {
    this.completeInFlight = true;
    this.paymentService
      .completePrepurchase(this.sessionToken)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.showSuccess(this.amountColones());
          this.settleComplete();
        },
        error: (err: unknown) => {
          const status = err instanceof HttpErrorResponse ? err.status : 0;

          if (status === 409) {
            this.handle409(err);
            this.settleComplete();
            return;
          }

          // ONVO aún no marca 'succeeded' tras el onSuccess (carrera breve): reintentar el complete.
          if (status === 422 && attempt < opts.maxRetries && !this.destroyed) {
            this.retryTimer = setTimeout(() => {
              this.retryTimer = null;
              if (!this.destroyed) this.runComplete(opts, attempt + 1);
            }, COMPLETE_RETRY_DELAY_MS);
            return;
          }

          opts.onInconclusive(status);
          this.settleComplete();
        },
      });
  }

  /** Cierra una llamada a `/complete`; si colgaba de un tick del poller, este puede programar el siguiente. */
  private settleComplete(): void {
    this.completeInFlight = false;
    this.closePollTick();
  }

  /**
   * Manejador ÚNICO del 409 (`/complete` y creación de intent), mapeo 1:1 sin inferir dinero. El
   * `default` es `chargeUnknown` (copy neutro con WhatsApp): nunca «no se cobró» ni «se agotó» sin un
   * código que lo diga.
   */
  private handle409(err: unknown): void {
    const body: unknown = err instanceof HttpErrorResponse ? err.error : null;
    const code = this.bodyField(body, 'error');
    const reason = this.bodyField(body, 'reason');

    switch (code) {
      case 'out_of_stock_refunded':
        this.failWithoutRetry(PRE_COPY.outOfStock);
        return;
      case 'refund_pending':
        this.failWithoutRetry(PRE_COPY.refundPending);
        return;
      case 'refund_failed':
        this.failWithoutRetry(PRE_COPY.refundFailed);
        return;
      case 'charge_not_eligible':
        if (reason === 'cancelled_recovering') {
          // Cobro HECHO que el webhook aún no ha promovido: no terminal, «Recibimos tu pago» + poller.
          this.chargeSeen.set(true);
          this.enterConfirming();
          return;
        }
        this.failWithoutRetry(reason === 'refunded' ? PRE_COPY.chargeRefunded : PRE_COPY.chargeUnknown);
        return;
      default:
        // Incluye el 409 de ONVO que `/complete` reenvía tal cual.
        this.failWithoutRetry(PRE_COPY.chargeUnknown);
    }
  }

  private bodyField(body: unknown, key: string): string | undefined {
    if (typeof body !== 'object' || body === null) return undefined;
    const value = (body as Record<string, unknown>)[key];
    return typeof value === 'string' ? value : undefined;
  }

  // ── Poller (D79): cadena de setTimeout, un tick cada 10 s, máximo 30, sin solapes ──

  /** Arranca el poller del panel «confirmando». Solo en navegador; no-op si ya corre. */
  private startPolling(): void {
    if (!isPlatformBrowser(this.platformId) || this.destroyed || this.pollActive) return;
    this.pollActive = true;
    this.pollTicks = 0;
    this.pollTickOpen = false;
    this.pollExhausted.set(false);
    this.schedulePollTick();
  }

  private stopPolling(): void {
    this.pollActive = false;
    this.pollTickOpen = false;
    if (this.pollTimer !== null) clearTimeout(this.pollTimer);
    this.pollTimer = null;
  }

  private schedulePollTick(): void {
    this.pollTimer = setTimeout(() => {
      this.pollTimer = null;
      this.pollTick();
    }, POLL_INTERVAL_MS);
  }

  private pollTick(): void {
    if (!this.pollActive || this.destroyed) return;

    // Un «Comprobar estado» manual en vuelo: este tick no lanza GET, se reprograma y NO gasta uno de los 30.
    if (this.checking()) {
      this.schedulePollTick();
      return;
    }

    this.pollTicks += 1;
    this.pollTickOpen = true;
    this.pollInFlight = true;
    this.paymentService
      .getPrepurchaseSession(this.sessionToken)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (s) => {
          this.pollInFlight = false;
          this.resolveSessionState(s, 'poll');
          this.settleManualWait(false);
          this.closePollTick();
        },
        // Un error de red o un 404 del tick cuenta como tick y el poller sigue.
        error: () => {
          this.pollInFlight = false;
          this.settleManualWait(true);
          this.closePollTick();
        },
      });
  }

  /**
   * Un clic en «Comprobar estado» con un tick en vuelo reutiliza el resultado del tick (sin segundo GET)
   * y recibe el mismo feedback que un check manual: estado terminal (ya pintado por el tick),
   * `stillConfirming` si sigue en el panel, o `checkFailed` si el tick falló.
   */
  private settleManualWait(failed: boolean): void {
    if (!this.manualWaiting) return;
    this.manualWaiting = false;
    this.checking.set(false);
    if (failed) {
      this.errorMsg.set(PRE_COPY.checkFailed);
    } else if (this.phase() === 'pending') {
      this.errorMsg.set(PRE_COPY.stillConfirming);
    }
  }

  /**
   * Cierra el tick en curso y, si el poller sigue vivo, programa el siguiente o lo da por agotado.
   * Con un `/complete` colgando del tick espera a que se resuelva (nunca dos peticiones en vuelo).
   */
  private closePollTick(): void {
    if (!this.pollTickOpen || this.completeInFlight) return;
    this.pollTickOpen = false;
    if (!this.pollActive || this.destroyed) return;
    if (this.pollTicks >= POLL_MAX_TICKS) {
      this.stopPolling();
      this.pollExhausted.set(true);
      return;
    }
    this.schedulePollTick();
  }

  private onWidgetError(data: OnvoErrorData): void {
    this.fail('decline', this.localizeOnvoError(data));
  }

  private onWidgetFormErrors(_data: OnvoFormErrorsData): void {
    this.submitting.set(false);
    if (this.phase() === 'confirming') this.enterPhase('ready');
  }

  private localizeOnvoError(data: OnvoErrorData): string {
    switch (data?.error?.code) {
      case 'card_declined':
        return CARD_COPY.declined;
      case 'insufficient_funds':
        return CARD_COPY.insufficientFunds;
      case 'expired_card':
        return CARD_COPY.expiredCard;
      case 'incorrect_cvc':
        return CARD_COPY.incorrectCvc;
      case 'processing_error':
        return CARD_COPY.processingError;
      default:
        return CARD_COPY.generic;
    }
  }

  private teardownWidget(): void {
    this.onvoInstance?.destroy?.();
    this.onvoInstance?.unmount?.();
    this.onvoInstance = null;
    this.mounted = false;
    this.paymentMount()?.nativeElement?.replaceChildren();
  }

  /** Éxito terminal: la precompra se creó. Muestra el mensaje verbatim con el abono formateado. */
  private showSuccess(amountColones: number): void {
    this.submitting.set(false);
    this.completing.set(false);
    this.expiresAt.set(null); // detiene el countdown
    this.successMessage.set(withMonto(PRE_COPY.success, formatCrc(amountColones)));
    this.enterPhase('success');
    // Refresca el saldo PP tras confirmarse el pago (solo si hay sesión: la
    // precompra de invitado no acumula, D14, pero no hace daño intentarlo).
    if (this.authService.isAuthenticated()) {
      this.pointsService.refresh();
    }
  }

  private fail(kind: PaymentFailureKind, message?: string | null): void {
    this.submitting.set(false);
    this.completing.set(false);
    this.attempts.update((n) => n + 1);
    this.enterPhase('failed');
    this.errorMsg.set(this.canRetry() ? this.errorCopy(kind, message) : CARD_COPY.escalation);
  }

  private failWithoutRetry(message: string): void {
    this.submitting.set(false);
    this.completing.set(false);
    this.noRetry.set(true);
    this.errorMsg.set(message);
    this.enterPhase('failed');
  }

  /** Traduce un error HTTP del bootstrap/creación de intent a un estado terminal o a fallo reintentable. */
  private resolveTerminalHttp(err: unknown, kind: PaymentFailureKind): void {
    const status = err instanceof HttpErrorResponse ? err.status : 0;
    // 410: la reserva venció antes de crear el intent → aviso de expiración terminal.
    if (status === 410) {
      this.failWithoutRetry(RESERVATION.expired);
      return;
    }
    // 409: reembolso, cobro no elegible u otro 409 → manejador único (D77).
    if (status === 409) {
      this.handle409(err);
      return;
    }
    // 403: gate de Early Access (Perrón Points) — defensa en profundidad, el
    // check primario ya vive en el modal de precompra (el gate re-valida en
    // creación de sesión, intent y creación final). Terminal, sin reintento:
    // reintentar sin loguearse o sin puntos no cambia nada.
    if (status === 403) {
      const mapped = err instanceof HttpErrorResponse ? mapEarlyAccessGateError(err) : null;
      if (mapped?.threshold !== undefined) {
        this.pointsService.recordThreshold(mapped.threshold);
      }
      this.failWithoutRetry(mapped?.message ?? CARD_COPY.declineFallback);
      return;
    }
    this.fail(kind);
  }

  private errorCopy(kind: PaymentFailureKind, message?: string | null): string {
    switch (kind) {
      case 'create':
        return CARD_COPY.createFailed;
      case 'timeout':
        return CARD_COPY.timeout;
      case 'network':
        return CARD_COPY.network;
      case 'decline':
        return message ?? CARD_COPY.declineFallback;
    }
  }

  /** El countdown llegó a 0. Si no hay un pago en vuelo, la reserva expiró: aviso terminal. */
  protected onCountdownExpired(): void {
    // Un pago en curso NO se interrumpe: el backend decide (gracia Q1 si aún hay cupo, o reembolso).
    if (this.submitting() || this.completing() || this.phase() === 'success') return;
    this.teardownWidget();
    this.failWithoutRetry(RESERVATION.expired);
  }

  /** Reintento sobre el MISMO intent: re-monta el widget (si la reserva sigue viva). */
  protected retry(): void {
    if (!this.canRetry()) return;
    this.errorMsg.set(null);
    this.cardReady.set(false);
    this.teardownWidget();
    if (this.intent()) {
      this.enterPhase('ready');
    } else {
      this.bootstrap();
    }
  }

  /**
   * Comprueba si el webhook ya creó la precompra (desde el panel `pending`). Con feedback. Comparte
   * `resolveSessionState` con el bootstrap y el poller, y la guarda con el tick del poller (no solapan).
   */
  protected checkSession(): void {
    if (this.checking() || this.completeInFlight) return;
    this.checking.set(true);
    this.errorMsg.set(null);
    // Con un tick en vuelo no sale un segundo GET: el clic espera al resultado del tick.
    if (this.pollInFlight) {
      this.manualWaiting = true;
      return;
    }
    this.paymentService
      .getPrepurchaseSession(this.sessionToken)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (s) => this.resolveSessionState(s, 'manual'),
        error: () => {
          this.checking.set(false);
          this.errorMsg.set(PRE_COPY.checkFailed);
        },
      });
  }

  /** Vuelve a la tienda (fallback / cierre tras éxito). */
  protected backToStore(): void {
    this.router.navigate(['/']);
  }

  /** Va al listado de precompras (CTA del éxito). */
  protected browsePrepurchases(): void {
    this.router.navigate(['/tienda/precompras']);
  }
}

/** Copy del fallo de arranque (sin token): reutiliza el genérico de tarjeta. */
const PRE_COPY_CREATE_FAIL = CARD_COPY.createFailed;
